import { action, computed, flow, makeObservable, observable, toJS } from 'mobx';
import { nanoid } from 'nanoid';

import { storageAgent } from 'src/api/agent';
import { ApiError, OperationAbortedError } from 'src/packages/errors';
import { equalsDeep } from 'src/packages/utils/equals-deep';
import { EventEmitter } from 'src/packages/utils/event-emitter';
import { getRandomNumber } from 'src/packages/utils/get-random-number';
import { hasValue } from 'src/packages/utils/has-value';
import { isObjectWithKeys } from 'src/packages/utils/is-object-with-keys';
import { numberGenerator } from 'src/packages/utils/number-generator';

import type {
  IEditModeDataManager,
  TControlView,
  TDirectoryObjectData,
  TRecord,
  TTableControlView,
  TTableView,
} from './types';
import type {
  IDirectoriesStorage,
  TGetObjectsParams,
  TStorageObject,
} from '../directories-storage-service/directories-storage-service';
import type { LocalizationService } from '../localization-service.ts';
import type { INotificationsService } from '../notifications-service';
import type { TableSettingsService } from '../table-settings-service';
import type { IMainServices } from 'src/app/types';
import type { DirectoryTableMediator } from 'src/components/directory-table/directory-table-mediator';

import { ControlRulesService } from '../control-rules-service';
import { TableSettingsManager } from '../table-settings-service/table-settings-manager';

import { DirectoriesServiceApi } from './api/directories-service-api';
import { deserializeDirectoryRecord } from './directory-service.utils';
import { DirectoryRecord } from './entities/directory-record.entity';
import { getControlsViews } from './mappers/get-controls-views';
import { OptionsService } from './options-service';
import { getObjectTypesFromControlView } from './utils';
import { ValidationService } from './validation-service';

export type TMainDirectoryEvents = {
  recordDataUpdated: {
    record: DirectoryRecord;
    fieldId: string;
  };
  newRecordsList: DirectoryRecord[];
};

const CHUNK_SIZE = 500;

export class MainDirectoryService {
  @observable private importAbortController: AbortController | null = null;

  private readonly editModeDataManager: IEditModeDataManager;
  private readonly api: DirectoriesServiceApi;
  private readonly mainServices: IMainServices;
  readonly localizationService: LocalizationService;
  readonly notificationsService: INotificationsService;
  readonly directoriesStorageService: IDirectoriesStorage;
  readonly tableSettingsService: TableSettingsService;
  readonly optionsService: OptionsService;
  readonly validationService: ValidationService;
  readonly controlRulesService: ControlRulesService;

  readonly view: TTableView;
  readonly directoryName: string;
  readonly entityId: string = nanoid(6);
  readonlycontrolRulesService: ControlRulesService;
  readonly numberGenerator: () => number = numberGenerator('negative');
  readonly eventEmitter: EventEmitter<TMainDirectoryEvents> = new EventEmitter();

  @observable private generalData: Record<string, unknown> = {};
  @observable generalDataErrors: Record<string, string> = {};
  @observable.shallow records: DirectoryRecord[] = [];
  private _recordsMap = new Map<number | string, DirectoryRecord>();
  @observable.shallow recordsActivity: Record<number | string, boolean> = {};

  recordControlsViews: TControlView[] = [];
  generalControlsViews: TControlView[] = [];
  complexControlsView: TTableControlView[] = [];
  controlsViewsMap = new Map<string, TControlView>();

  @observable isDataFetching = false;
  @observable isImporting = false;
  @observable isAdditionalDirectoriesFetching = false;
  abortControllers = new Map<string, VoidFunction>();
  @observable tableSettingsManager: TableSettingsManager;
  @observable directoryViewMediator: DirectoryTableMediator | null = null;
  readonly isImportAvailable: boolean;

  @observable.ref status:
    | { type: 'initial' }
    | { type: 'ready' }
    | { type: 'error'; message: 'generalControlsNotFilledIn' | 'noData' } = { type: 'initial' };

  constructor(
    mainServices: IMainServices,
    editModeDataManager: IEditModeDataManager,
    tableSettingsService: TableSettingsService,
    directoryName: string,
    view: TTableView,
  ) {
    this.mainServices = mainServices;
    this.editModeDataManager = editModeDataManager;
    this.tableSettingsService = tableSettingsService;
    this.localizationService = mainServices.localizationService;
    this.directoriesStorageService = mainServices.directoriesStorage;
    this.optionsService = new OptionsService(mainServices.notificationsService, mainServices.directoriesStorage);
    this.validationService = new ValidationService(mainServices.localizationService);
    this.notificationsService = mainServices.notificationsService;
    this.view = view;
    this.directoryName = directoryName;
    this.tableSettingsManager = new TableSettingsManager(tableSettingsService, directoryName, this);
    this.controlRulesService = new ControlRulesService(mainServices.directoriesStorage, this.validationService);
    this.isImportAvailable = !!view.allowImport;
    this.api = new DirectoriesServiceApi(storageAgent);

    makeObservable(this);
  }

  get recordsMap(): Map<number | string, DirectoryRecord> {
    return this._recordsMap;
  }

  @computed
  get generalDataValues(): Record<string, unknown> {
    return this.generalData;
  }

  getRecordById(id: number | string): DirectoryRecord | null {
    const record = this._recordsMap.get(id) ?? null;

    return record;
  }

  @action.bound
  updateGeneralData(data: Record<string, unknown>): void {
    this.generalData = data;
  }

  @action.bound
  setGeneralDataErrors(data: Record<string, string>): void {
    this.generalDataErrors = data;
  }

  @flow.bound
  async *loadAdditionalDirectory(): Promise<void> {
    this.isAdditionalDirectoriesFetching = true;

    const controlViews = [...this.generalControlsViews, ...this.recordControlsViews];

    if (!controlViews.length) {
      return;
    }

    const loadObjects = async (objectType: string) => {
      try {
        await this.directoriesStorageService.loadObjects(objectType, true);
      } catch (e) {
        if (e instanceof OperationAbortedError) {
          return;
        }

        console.error(e);

        if (e instanceof ApiError && e.message) {
          this.notificationsService.showErrorMessage(e.message);
          return;
        }

        this.notificationsService.showErrorMessageT('directory:errors.data.failedToLoadData');
      }
    };

    const requests: Promise<void>[] = [];

    for (const controlView of controlViews) {
      // временно отключенный функционал
      // if (columnsVisibility[refObjectType] === false) {
      //   continue;
      // }

      const objectTypes = getObjectTypesFromControlView(controlView);

      objectTypes.forEach((refObjectType) => {
        requests.push(loadObjects(refObjectType));
      });
    }

    for (const complexControlView of this.complexControlsView) {
      if (complexControlView.control === 'Table') {
        const { recordControls, topControls } = getControlsViews(complexControlView);

        [...recordControls, ...topControls].forEach((controlView) => {
          const objectTypes = getObjectTypesFromControlView(controlView);

          objectTypes.forEach((refObjectType) => {
            requests.push(loadObjects(refObjectType));
          });
        });
      }
    }

    await Promise.allSettled(requests);
    yield;

    this.isAdditionalDirectoriesFetching = false;
  }

  @computed
  get hasChanges(): boolean {
    return this.records.some((record) => record.recordChangeState);
  }

  @computed
  get newRecords(): DirectoryRecord[] {
    return this.records.filter((record) => record.recordChangeState === 'new');
  }

  @computed
  get updatedRecords(): DirectoryRecord[] {
    return this.records.filter((record) => record.recordChangeState === 'hasChanges');
  }

  @computed
  get areGeneralDataFilledIn(): boolean {
    if (!isObjectWithKeys(this.generalData) && !this.generalControlsViews.length) {
      return true;
    }

    return this.generalControlsViews.every((v) => hasValue(this.generalData[v.fieldId]));
  }

  @action.bound
  onGeneralDataValueChange(fieldId: string, value: unknown): void {
    if (equalsDeep(this.generalData[fieldId], value)) {
      return;
    }

    this.updateGeneralData({ ...this.generalData, [fieldId]: value });

    this.tableSettingsService.getOrCreateSettings().then((settings) => {
      this.tableSettingsService.updateSettings(this.directoryName, {
        ...settings?.[this.directoryName],
        fieldValues: { ...this.generalData },
      });
    });

    this.loadData();
  }

  @action.bound
  onGeneralControlBlur(fieldId: string): void {}

  @action.bound
  onControlBlur(record: DirectoryRecord, fieldId: string): void {
    this.validationService.validateField(record, fieldId);
  }

  @action.bound
  onValueChange(record: DirectoryRecord, fieldId: string, value: unknown): void {
    if (equalsDeep(record.data[fieldId], value)) {
      return;
    }

    const { [fieldId]: fieldErrors, ...rest } = record.fieldsErrors;
    record.updateFieldsErrors(rest);
    record.updateData({ [fieldId]: value });

    this.controlRulesService.onControlValueChange(
      record,
      this.controlsViewsMap,
      fieldId,
      value,
      (record: DirectoryRecord, fieldId: string, value: unknown) => {
        this.onValueChange(record, fieldId, value);
      },
    );

    this.eventEmitter.emit('recordDataUpdated', { record, fieldId });
    this.editModeDataManager.saveEditModeData(this);
  }

  @action.bound
  cancelImport(): void {
    this.importAbortController?.abort();
    this.importAbortController = null;
  }

  @flow.bound
  async *importData(file: File, onSuccess?: () => void) {
    this.isImporting = true;
    const abortController = new AbortController();
    this.importAbortController = abortController;

    try {
      const formData = new FormData();
      formData.append('file', file);

      await this.api.importData(formData);
      yield;

      this.loadData();
      onSuccess?.();
    } catch (e) {
      yield;

      if (e instanceof OperationAbortedError) {
        return;
      }

      if (e instanceof ApiError && e.message) {
        this.notificationsService.showErrorMessage(e.message);
        return;
      }

      console.error(e);
      this.notificationsService.showErrorMessageT('Не удалось импортировать данные');
    } finally {
      this.isImporting = false;
      this.importAbortController = null;
    }
  }

  @flow.bound
  async *initializeControls() {
    const { recordControls, topControls, complexControls } = getControlsViews(this.view);
    const controls = [...recordControls, ...topControls];

    this.recordControlsViews = recordControls;
    this.generalControlsViews = topControls;
    this.complexControlsView = complexControls;
    this.controlRulesService.mapRulesFromControlViews(controls);

    controls.forEach((controlView) => {
      this.controlsViewsMap.set(controlView.fieldId, controlView);

      const localeKey = `labels:${controlView.fieldId}.label`;
      const defaultLabel = controlView.defaultLabel || controlView.defaultValue;

      if (!this.localizationService.i18n.exists(localeKey) && defaultLabel) {
        this.localizationService.i18n.languages.forEach((lang) => {
          this.localizationService.i18n.addResource(lang, 'labels', `${controlView.fieldId}.label`, defaultLabel);
        });
      }
    });

    complexControls.forEach((view) => {
      this.controlsViewsMap.set(view.fieldId, view);
    });

    await this.loadAdditionalDirectory();
    yield;

    const complexControlsInnerControls = complexControls.reduce((acc: TControlView[], complexControl) => {
      if (complexControl.control === 'Table') {
        const { recordControls, topControls } = getControlsViews(complexControl);

        recordControls.forEach((v) => acc.push(v));
        topControls.forEach((v) => acc.push(v));
      }

      return acc;
    }, []);

    this.optionsService.createInitialOptions([...controls, ...complexControlsInnerControls]);
    this.validationService.setControlViews([...controls, ...complexControls]);
    this.validationService.setRequiredFields(this.view.required);

    const settings = await this.tableSettingsService.getOrCreateSettings();
    yield;

    if (settings) {
      topControls.forEach((view) => {
        const savedValue = settings[this.directoryName]?.fieldValues?.[view.fieldId];

        if (!hasValue(this.generalData[view.fieldId]) && hasValue(savedValue)) {
          this.generalData = { ...this.generalData, [view.fieldId]: savedValue };
        }
      });
    }

    this.status = { type: 'ready' };
  }

  @action.bound
  discardChanges(): void {
    const initialRecords: DirectoryRecord[] = [];
    const controlViews = [...this.recordControlsViews, ...this.complexControlsView];

    for (const record of this.records) {
      record.updateFieldsErrors({});

      if (record.recordChangeState === 'new') {
        continue;
      }

      if (record.recordChangeState === 'hasChanges') {
        for (const controlView of controlViews) {
          if (!controlView.attrName) {
            continue;
          }

          const initialValue = record.initialData?.[controlView.fieldId];

          this.onValueChange(record, controlView.fieldId, initialValue);
        }

        initialRecords.push(record);
        continue;
      }

      initialRecords.push(record);
    }

    this.records = initialRecords;
  }

  @action.bound
  resetRecord(record: DirectoryRecord): void {
    if (record.recordChangeState !== 'hasChanges') {
      console.error('deleting record has no changes');
      return;
    }

    for (const fieldView of [...this.recordControlsViews, ...this.complexControlsView]) {
      if (!fieldView.attrName) {
        continue;
      }

      const initialValue = record.initialData?.[fieldView.fieldId];

      this.onValueChange(record, fieldView.fieldId, initialValue);
    }

    this.editModeDataManager.saveEditModeData(this);
  }

  @action.bound
  deleteRecord(record: DirectoryRecord): void {
    if (record.recordChangeState !== 'new') {
      console.error('deleting record is not new');
      return;
    }
    const index = this.records.indexOf(record);
    if (index >= 0) {
      this.records.splice(index, 1);
      this._recordsMap.delete(record.recordId);
      this.directoryViewMediator?.onDeleteRecord?.(record);
    }
    this.editModeDataManager.saveEditModeData(this);
  }

  @action.bound
  createRecords(initialValues: Partial<TRecord>[]): DirectoryRecord[] {
    const records: DirectoryRecord[] = [];
    const activity: Record<number, boolean> = {};
    const recordsMap = new Map<number, DirectoryRecord>();

    const requiredRecords = this.validationService.requiredFieldNames;
    const controlViews = [...this.recordControlsViews, ...this.complexControlsView];

    initialValues.forEach((initRecord) => {
      const id = initRecord?.id ?? -getRandomNumber();

      activity[id] = true;

      const newRecord = new DirectoryRecord(controlViews, initRecord, id);

      this.recordControlsViews.forEach((v) => {
        this.controlRulesService.onControlValueChange(
          newRecord,
          this.controlsViewsMap,
          v.fieldId,
          initRecord[v.fieldId],
          (record: DirectoryRecord, fieldId: string, value: unknown) => {
            this.onValueChange(record, fieldId, value);
          },
        );
      });

      records.push(newRecord);
      recordsMap.set(id, newRecord);
    });

    this.recordsActivity = { ...this.recordsActivity, ...activity };
    Array.from(recordsMap.entries()).forEach(([id, record]) => this._recordsMap.set(id, record));

    for (const newRecord of records) {
      this.records.push(newRecord);
    }

    this.editModeDataManager.saveEditModeData(this);

    return records;
  }

  @action.bound
  addNewRecord(initialValue?: Partial<TRecord>): DirectoryRecord {
    const id = initialValue?.id ?? -getRandomNumber();

    const controlViews = [...this.recordControlsViews, ...this.complexControlsView];

    this.recordsActivity[id] = true;

    const newRecord = new DirectoryRecord(controlViews, null, id);
    this.records.push(newRecord);

    this._recordsMap.set(id, newRecord);

    this.editModeDataManager.saveEditModeData(this);

    controlViews.forEach((v) => {
      const savedValue = (() => {
        if (!initialValue) {
          return null;
        }

        if (v.attrName && hasValue(initialValue[v.attrName])) {
          return initialValue[v.attrName];
        }

        if (hasValue(initialValue[v.fieldId])) {
          return initialValue[v.fieldId];
        }

        return null;
      })();

      this.onValueChange(newRecord, v.fieldId, savedValue);
    });

    this.directoryViewMediator?.onAddNewRecord?.(newRecord);

    return newRecord;
  }

  @action.bound
  setDirectoryViewMediator(mediator: DirectoryTableMediator | null): void {
    this.directoryViewMediator = mediator;
  }

  @flow.bound
  async *updateDirectoryStatus(status: boolean, recordId: number | string) {
    const newStatus = status ? 'ACTIVE' : 'DELETED';

    this.recordsActivity[recordId] = status;

    try {
      await this.directoriesStorageService.changeObjectStatus(this.directoryName, recordId, newStatus);
      yield;
    } catch (e) {
      yield;

      if (e instanceof OperationAbortedError) {
        return;
      }

      if (e instanceof ApiError && e.message) {
        this.notificationsService.showErrorMessage(e.message);
        return;
      }

      this.notificationsService.showErrorMessageT('directory:errors.data.failedToUpdateStatus');
    }
  }

  @flow.bound
  async *loadData(params?: TGetObjectsParams) {
    if (!this.areGeneralDataFilledIn) {
      this.status = { type: 'error', message: 'generalControlsNotFilledIn' };

      return;
    } else {
      this.status = { type: 'ready' };
    }

    const copiedParams: TGetObjectsParams = {
      ...params,
      filterMap: { ...params?.filterMap, ...this.generalDataValues },
    };

    this.isDataFetching = true;

    this.abortControllers.get('loadData')?.();

    const abortController = new AbortController();
    this.abortControllers.set('loadData', () => abortController.abort());

    try {
      const data = await this.directoriesStorageService.loadObjects<TDirectoryObjectData>(
        this.directoryName,
        false,
        copiedParams,
        abortController.signal,
      );
      yield;

      const newRecordsActivity: Record<number, boolean> = {};

      const editModeData = this.editModeDataManager.getSavedEditModeData(this);
      const records: DirectoryRecord[] = [];
      const recordsMap = new Map<number | string, DirectoryRecord>();
      const controlViews = [...this.recordControlsViews, ...this.complexControlsView];

      const deserialize = (record: TStorageObject<TDirectoryObjectData>) => {
        const controlValues = deserializeDirectoryRecord(record.data, controlViews);

        newRecordsActivity[record.id] = record.status === 'ACTIVE';
        const newRecord = new DirectoryRecord(controlViews, controlValues, record.id);

        const editModeRecordData = toJS(editModeData?.updatedRecords[newRecord.recordId]);
        const editModeControlValues = editModeRecordData
          ? deserializeDirectoryRecord(editModeRecordData, controlViews)
          : null;

        const recordData = { ...controlValues, ...(editModeControlValues ?? {}) };

        controlViews.forEach((v) => {
          const value = recordData[v.fieldId];

          this.controlRulesService.onControlValueChange(
            newRecord,
            this.controlsViewsMap,
            v.fieldId,
            value,
            (record: DirectoryRecord, fieldId: string, value: unknown) => {
              recordData[fieldId] = value;
            },
          );
        });

        newRecord.updateData(recordData);

        records.push(newRecord);
        recordsMap.set(newRecord.recordId, newRecord);
      };

      const recordDeserializationPromises: Promise<void>[] = [];

      const chunksCount = Math.ceil(data.length / CHUNK_SIZE);

      for (let i = 0; i < chunksCount; i++) {
        const startIndex = i * CHUNK_SIZE;

        const slice = data.slice(startIndex, startIndex + CHUNK_SIZE);

        recordDeserializationPromises.push(
          new Promise<void>((resolve) => {
            setTimeout(() => {
              slice.forEach(deserialize);

              resolve();
            }, 0);
          }),
        );
      }

      await Promise.all(recordDeserializationPromises);
      yield;

      if (editModeData) {
        for (const recordId in editModeData.newRecords) {
          const id = Number(recordId);
          const rawRecord = editModeData.newRecords[recordId];
          const controlValues = deserializeDirectoryRecord(rawRecord, controlViews);

          newRecordsActivity[id] = true;
          const newRecord = new DirectoryRecord(controlViews, controlValues, id);

          const recordData = { ...controlValues };

          for (const fieldId in controlValues) {
            this.controlRulesService.onControlValueChange(
              newRecord,
              this.controlsViewsMap,
              fieldId,
              controlValues[fieldId],
              (record, fieldId, value) => {
                recordData[fieldId] = value;
              },
            );
          }

          newRecord.updateData(recordData);

          records.push(newRecord);
          recordsMap.set(id, newRecord);
        }
      }

      this.recordsActivity = newRecordsActivity;
      this.records = records;
      this._recordsMap = recordsMap;

      this.eventEmitter.emit('newRecordsList', records);
    } catch (e) {
      yield;

      if (e instanceof OperationAbortedError) {
        return;
      }

      console.error(e);

      this.status = { type: 'error', message: 'noData' };

      if (e instanceof ApiError && e.message) {
        this.notificationsService.showErrorMessage(e.message);
        return;
      }

      this.notificationsService.showErrorMessageT('directory:errors.data.failedToLoadData');
    } finally {
      this.isDataFetching = false;
    }
  }

  init = (): VoidFunction => {
    const optionsServiceDisposer = this.optionsService.init();

    // this.tableSettingsManager.checkSettings(this.controlsViewsMap);

    return () => {
      optionsServiceDisposer();
      this.abortControllers.forEach((fn) => fn());
      this.abortControllers.clear();
    };
  };
}
